import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Download, AlertCircle, FileArchive } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ModuleAnswerReviewContent } from '@/components/results/ModuleAnswerReviewContent';
import { getAnswerAnalysisSettings } from '@/lib/answerAnalysisSettingsApi';
import {
  commitModuleReanalysis,
  previewModuleReanalysis,
  type ReanalysisPayload,
  type ReanalysisProvider,
} from '@/lib/adminReanalysisApi';
import { downloadAnswerHtmlArchive } from '@/lib/answerArchiveApi';
import type { ModuleAnswer, ModuleResult } from '@/types/simulation-results';
import {
  normalizeAnalysisBreakdown,
  type AnalysisBreakdown,
} from '@/lib/analysisBreakdown';
import { cn } from '@/lib/utils';

const PROVIDER_OPTIONS: ReadonlyArray<{ value: ReanalysisProvider; label: string }> = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Gemini' },
];

interface ReanalyseModuleDialogProps {
  readonly open: boolean;
  readonly sessionId: string;
  readonly moduleName: string;
  readonly answers: readonly ModuleAnswer[];
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void> | void;
}

function applyPreviewedAnalysis(
  answers: readonly ModuleAnswer[],
  payload: ReanalysisPayload,
): ModuleAnswer[] {
  return answers.map((answer) => {
    const breakdown = payload.analysis_breakdowns[answer.id];
    if (!breakdown) {
      return answer;
    }
    const grade = payload.grades[answer.id];
    return {
      ...answer,
      analysis_breakdown: breakdown as unknown as AnalysisBreakdown,
      points: typeof grade === 'number' ? grade * 100 : answer.points,
      is_correct: typeof grade === 'number' ? grade >= 0.6 : answer.is_correct,
    } satisfies ModuleAnswer;
  });
}

function getAnswerScore(answer: ModuleAnswer): number | null {
  const breakdown = normalizeAnalysisBreakdown(answer.analysis_breakdown);
  const fromBreakdown = breakdown?.final_score;
  if (typeof fromBreakdown === 'number' && Number.isFinite(fromBreakdown)) {
    return fromBreakdown;
  }
  if (typeof answer.points === 'number' && Number.isFinite(answer.points)) {
    return answer.points;
  }
  return null;
}

interface AnswerDiffRow {
  readonly id: string;
  readonly index: number;
  readonly oldScore: number | null;
  readonly newScore: number | null;
  readonly delta: number | null;
}

function buildDiffRows(
  original: readonly ModuleAnswer[],
  previewed: readonly ModuleAnswer[],
): AnswerDiffRow[] {
  const previewedById = new Map(previewed.map((a) => [a.id, a]));
  return original.map((answer, idx) => {
    const oldScore = getAnswerScore(answer);
    const previewedAnswer = previewedById.get(answer.id);
    const newScore = previewedAnswer ? getAnswerScore(previewedAnswer) : null;
    const delta =
      typeof oldScore === 'number' && typeof newScore === 'number'
        ? newScore - oldScore
        : null;
    return {
      id: answer.id,
      index: idx + 1,
      oldScore,
      newScore,
      delta,
    } satisfies AnswerDiffRow;
  });
}

function formatScore(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)}%`;
}

function deltaClass(delta: number | null): string {
  if (delta === null) return 'bg-muted text-muted-foreground';
  if (delta >= 5) return 'bg-emerald-100 text-emerald-700';
  if (delta <= -5) return 'bg-rose-100 text-rose-700';
  return 'bg-amber-100 text-amber-700';
}

// Mirrors the backend's module-score formula in
// answer_analysis_service.py::_update_answer_grades — a simple mean of the
// per-answer grades, scaled to 0-100. Used to feed ModuleAnswerReviewContent
// with a synthesized sessionModules entry so the "Total grade" badge shows
// the score that will be persisted on Save instead of the pass-rate fallback.
function computeModuleAverageScore(answers: readonly ModuleAnswer[]): number | null {
  const scores: number[] = [];
  for (const answer of answers) {
    const score = getAnswerScore(answer);
    if (typeof score === 'number' && Number.isFinite(score)) {
      scores.push(score);
    }
  }
  if (scores.length === 0) return null;
  return scores.reduce((sum, s) => sum + s, 0) / scores.length;
}

function buildSyntheticSessionModules(
  moduleName: string,
  answers: readonly ModuleAnswer[],
): ModuleResult[] {
  const avg = computeModuleAverageScore(answers);
  if (avg === null) return [];
  return [
    {
      module: moduleName.toLowerCase() as ModuleResult['module'],
      score: avg,
      maxScore: 100,
      percentage: avg,
      passed: avg >= 60,
      is_graded: true,
    },
  ];
}

function formatDelta(delta: number | null): string {
  if (delta === null || !Number.isFinite(delta)) return '—';
  const rounded = Math.round(delta);
  if (rounded === 0) return '±0';
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ReanalyseModuleDialog({
  open,
  sessionId,
  moduleName,
  answers,
  onClose,
  onSaved,
}: Readonly<ReanalyseModuleDialogProps>) {
  const [provider, setProvider] = useState<ReanalysisProvider>('openai');
  const [allowed, setAllowed] = useState<readonly string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingArchive, setIsExportingArchive] = useState(false);
  const [preview, setPreview] = useState<ReanalysisPayload | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setErrorMessage(null);
    void getAnswerAnalysisSettings().then((settings) => {
      if (!settings) return;
      setAllowed(settings.allowedProviders);
      if (
        settings.allowedProviders.length > 0 &&
        !settings.allowedProviders.includes(provider)
      ) {
        setProvider(settings.allowedProviders[0] as ReanalysisProvider);
      }
    });
  }, [open, provider]);

  const previewedAnswers = useMemo(
    () => (preview ? applyPreviewedAnalysis(answers, preview) : answers),
    [answers, preview],
  );
  const previewModelLabel = preview?.analysisModel || preview?.provider || null;

  const diffRows = useMemo(
    () => (preview ? buildDiffRows(answers, previewedAnswers) : []),
    [answers, preview, previewedAnswers],
  );

  const currentSessionModules = useMemo(
    () => buildSyntheticSessionModules(moduleName, answers),
    [answers, moduleName],
  );
  const previewSessionModules = useMemo(
    () => buildSyntheticSessionModules(moduleName, previewedAnswers),
    [moduleName, previewedAnswers],
  );

  const handleRun = useCallback(async () => {
    setIsRunning(true);
    setErrorMessage(null);
    try {
      const result = await previewModuleReanalysis(sessionId, moduleName, provider);
      if (!result) {
        setErrorMessage('Re-analysis failed. Check the server logs and try again.');
        return;
      }
      if (Object.keys(result.analysis_breakdowns).length === 0) {
        setErrorMessage(
          result.errors.length > 0
            ? result.errors[0].error
            : 'Re-analysis produced no results.',
        );
      }
      setPreview(result);
    } finally {
      setIsRunning(false);
    }
  }, [moduleName, provider, sessionId]);

  const handleExport = useCallback(() => {
    if (!preview) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const modelSegment = preview.analysisModel || preview.provider;
    downloadJson(`reanalysis_${sessionId}_${moduleName}_${modelSegment}_${stamp}.json`, {
      sessionId,
      module: moduleName,
      exportedAt: new Date().toISOString(),
      ...preview,
      analysisModel: preview.analysisModel ?? null,
    });
    toast.success('Exported re-analysis JSON');
  }, [moduleName, preview, sessionId]);

  const handleExportHtml = useCallback(async () => {
    if (!preview) return;
    setIsExportingArchive(true);
    try {
      await downloadAnswerHtmlArchive({
        sessionId,
        modules: [moduleName],
        source: 'reanalysis_preview',
        preview,
        filenameHint: `reanalysis-${moduleName}-${preview.provider}`,
        metadata: {
          module: moduleName,
          provider: preview.provider,
          analysisModel: preview.analysisModel ?? null,
        },
      });
      toast.success('Exported re-analysis HTML ZIP');
    } finally {
      setIsExportingArchive(false);
    }
  }, [moduleName, preview, sessionId]);

  const handleSave = useCallback(async () => {
    if (!preview) return;
    setIsSaving(true);
    try {
      const result = await commitModuleReanalysis(sessionId, moduleName, preview);
      if (!result) return;
      toast.success(
        `Saved re-analysis (${result.successCount} answer${
          result.successCount === 1 ? '' : 's'
        } updated)`,
      );
      await onSaved();
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [moduleName, onClose, onSaved, preview, sessionId]);

  const handleDiscard = useCallback(() => {
    setPreview(null);
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDiscard();
      }}
    >
      <DialogContent className="flex h-[92vh] max-h-[92vh] w-[96vw] max-w-[110rem] flex-col overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="font-ui">Re-analyse {moduleName}</DialogTitle>
          <DialogDescription className="font-ui">
            Re-runs the same analysis pipeline with the chosen provider. Nothing is
            written until you click Save.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b border-border bg-muted/30 px-5 py-3">
          <div className="flex flex-wrap items-center gap-4">
            <Label className="font-ui text-sm font-medium">Model</Label>
            <RadioGroup
              value={provider}
              onValueChange={(v) => setProvider(v as ReanalysisProvider)}
              className="flex flex-wrap items-center gap-4"
            >
              {PROVIDER_OPTIONS.map((option) => {
                const disabled = allowed.length > 0 && !allowed.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 text-sm font-ui ${
                      disabled ? 'opacity-50' : ''
                    }`}
                  >
                    <RadioGroupItem value={option.value} disabled={disabled} />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </RadioGroup>
            <Button
              size="sm"
              onClick={handleRun}
              disabled={isRunning || isSaving}
              className="ml-auto"
            >
              {isRunning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
                </>
              ) : preview ? (
                'Re-run'
              ) : (
                'Run analysis'
              )}
            </Button>
          </div>
          {errorMessage ? (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
          {preview ? (
            <div className="flex min-h-0 min-w-max flex-1">
              <div className="flex min-h-0 w-[70rem] shrink-0 flex-col border-r border-border">
                <div className="border-b border-border bg-muted/40 px-4 py-2 font-ui text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Current analysis
                </div>
                <ModuleAnswerReviewContent
                  layout="dialog"
                  sessionId={sessionId}
                  moduleName={moduleName}
                  answers={answers as ModuleAnswer[]}
                  sessionModules={currentSessionModules}
                  itemRatingsMode="learner"
                  className="min-h-0 flex-1"
                />
              </div>
              <div className="flex min-h-0 w-[70rem] shrink-0 flex-col">
                <div className="border-b border-border bg-primary/10 px-4 py-2 font-ui text-xs font-semibold uppercase tracking-wide text-primary">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>Re-analysis ({preview.provider})</span>
                    {previewModelLabel && (
                      <Badge variant="outline" className="font-ui text-[10px] normal-case">
                        Model: {previewModelLabel}
                      </Badge>
                    )}
                  </div>
                </div>
                {diffRows.length > 0 && (
                  <div className="border-b border-border bg-primary/5 px-4 py-2.5">
                    <div className="mb-1.5 font-ui text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Per-question score · current → re-analysed
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {diffRows.map((row) => (
                        <div
                          key={row.id}
                          className="font-ui inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[11px]"
                          title={`Q${row.index}: ${formatScore(row.oldScore)} → ${formatScore(row.newScore)}`}
                        >
                          <span className="font-semibold text-foreground">Q{row.index}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {formatScore(row.oldScore)}
                          </span>
                          <span className="text-muted-foreground/60">→</span>
                          <span className="tabular-nums font-semibold text-foreground">
                            {formatScore(row.newScore)}
                          </span>
                          <span
                            className={cn(
                              'rounded px-1 py-0.5 text-[10px] font-bold tabular-nums',
                              deltaClass(row.delta),
                            )}
                          >
                            {formatDelta(row.delta)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <ModuleAnswerReviewContent
                  layout="dialog"
                  sessionId={sessionId}
                  moduleName={moduleName}
                  answers={previewedAnswers}
                  sessionModules={previewSessionModules}
                  itemRatingsMode="learner"
                  className="min-h-0 flex-1"
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center p-10 text-center font-ui text-sm text-muted-foreground">
              Select a model and click <span className="mx-1 font-medium">Run analysis</span> to
              generate a fresh analysis.
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-5 py-3 sm:justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={!preview}
            >
              <Download className="mr-2 h-4 w-4" /> Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportHtml}
              disabled={!preview || isExportingArchive}
            >
              {isExportingArchive ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileArchive className="mr-2 h-4 w-4" />
              )}
              HTML ZIP
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleDiscard} disabled={isSaving}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!preview || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
